import React, { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { BrandLogo } from "@/components/BrandLogo";
import { LayoutDashboard, Inbox, Tags, Palette, Code2, Building2, CreditCard, Shield, LogOut, Menu, ClipboardList } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

const NAV = [
  { to: "/app", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/app/leads", label: "Leads", icon: Inbox },
  { to: "/app/field-quotes", label: "Field Quotes", icon: ClipboardList },
  { to: "/app/services", label: "Services & Pricing", icon: Tags },
  { to: "/app/appearance", label: "Widget Appearance", icon: Palette },
  { to: "/app/install", label: "Install", icon: Code2 },
  { to: "/app/business", label: "Business Profile", icon: Building2 },
  { to: "/app/billing", label: "Billing", icon: CreditCard },
];

function NavItems({ onClick }: { onClick?: () => void }) {
  const { user } = useAuth();
  const admin = { to: "/app/admin", label: "Super Admin", icon: Shield };
  const items = user && user.is_platform_admin ? (user.org ? [...NAV, admin] : [admin]) : NAV;
  return (
    <nav className="space-y-1">
      {items.map((n: any) => (
        <NavLink key={n.to} to={n.to} end={n.end} onClick={onClick} data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 h-10 text-sm transition-colors ${isActive ? "bg-primary text-primary-foreground" : "text-foreground/80 hover:bg-secondary"}`}>
          <n.icon className="h-4 w-4" /> {n.label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const u = user || null;
  const signOut = async () => { await logout(); nav("/login"); };
  const resend = async () => {
    const { data } = await api.post("/auth/resend-verification").catch(() => ({ data: { sent: false } }));
    toast[data.sent ? "success" : "error"](data.sent ? "Verification email sent." : "Could not send right now. Try later.");
  };
  return (
    <div className="min-h-screen lg:grid lg:grid-cols-[248px_1fr]">
      <aside className="hidden lg:flex flex-col border-r bg-card p-4 sticky top-0 h-screen">
        <div className="px-3 py-2 mb-6">
          <Link to="/app" aria-label="Instant Quote Wizard dashboard"><BrandLogo className="h-16" testid="sidebar-brand-logo" /></Link>
          <p className="text-xs text-muted-foreground truncate mt-2" data-testid="sidebar-org-name">{u?.org?.name}</p>
        </div>
        <NavItems />
        <div className="mt-auto border-t pt-4">
          <p className="px-3 text-xs text-muted-foreground truncate mb-2">{u?.email}</p>
          <Button variant="ghost" className="w-full justify-start" onClick={signOut} data-testid="logout-button"><LogOut className="h-4 w-4 mr-2" />Sign out</Button>
        </div>
      </aside>
      <header data-sticky-header className="lg:hidden flex items-center justify-between px-4 h-14 border-b bg-card sticky top-0 z-30">
        <Link to="/app" className="flex items-center gap-2 min-w-0" aria-label="Instant Quote Wizard dashboard">
          <BrandLogo className="h-10 shrink-0" testid="mobile-brand-logo" />
          <span className="text-xs text-muted-foreground truncate border-l pl-2">{u?.org?.name}</span>
        </Link>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild><Button variant="ghost" size="icon" className="h-11 w-11" data-testid="mobile-menu-button"><Menu /></Button></SheetTrigger>
          <SheetContent side="left" className="w-72 p-4">
            <div className="mb-6 mt-2"><BrandLogo className="h-14" testid="sheet-brand-logo" /></div>
            <NavItems onClick={() => setOpen(false)} />
            <Button variant="ghost" className="w-full justify-start mt-6" onClick={signOut} data-testid="mobile-logout-button"><LogOut className="h-4 w-4 mr-2" />Sign out</Button>
          </SheetContent>
        </Sheet>
      </header>
      <main className="min-w-0">
        {u && !u.email_verified && (
          <div className="bg-amber-50 border-b border-amber-200 text-amber-900 text-sm px-6 py-2.5 flex flex-wrap gap-2 items-center" data-testid="verify-banner">
            Please confirm your email address ({u.email}).
            <button className="underline" onClick={resend} data-testid="resend-verification-button">Resend link</button>
          </div>
        )}
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8 lg:py-10"><Outlet /></div>
      </main>
    </div>
  );
}

export function PageHead({ title, sub, actions }: { title: string; sub?: string; actions?: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold" data-testid="page-title">{title}</h1>
        {sub && <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{sub}</p>}
      </div>
      {actions}
    </div>
  );
}
